import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { X } from "lucide-react";
import { useSelector } from "react-redux";

const DeliveryPartnerSubscriptionModal = ({
  open,
  setOpen,
  product,
  quantity = 1,
  onConfirm,
  initialData,
}) => {
  const { coreSettings } = useSelector((state) => state.auth);

  // Calculate total price based on quantity
  const totalPrice = useMemo(() => {
    if (product?.price && quantity) {
      return (parseFloat(product.price) * quantity).toFixed(2);
    }
    return "0.00";
  }, [product?.price, quantity]);

  const [frequency, setFrequency] = useState(initialData?.frequency || "weekly");
  const [customNotes, setCustomNotes] = useState(initialData?.customNotes || "");

  // Update state when initialData changes
  useEffect(() => {
    if (initialData) {
      setFrequency(initialData.frequency || "weekly");
      setCustomNotes(initialData.customNotes || "");
    }
  }, [initialData]);

  // Get frequency options from settings API
  const frequencyOptions = useMemo(() => {
    if (coreSettings?.SUBSCRIPTION_FREQUENCIES?.length > 0) {
      return coreSettings.SUBSCRIPTION_FREQUENCIES.filter(
        (freq) => freq.status === 1
      ) // Only active frequencies
        .map((freq) => ({
          value: freq.short_name,
          label: freq.name,
          days: freq.days,
          description: freq.description,
        }));
    }
    // Fallback to default options if API data not available
    return [
      { value: "weekly", label: "Weekly", days: 7 },
      { value: "biweekly", label: "Bi-Weekly", days: 14 },
      { value: "monthly", label: "Monthly", days: 30 },
    ];
  }, [coreSettings]);

  const handleConfirm = () => {
    onConfirm({
      frequency,
      customNotes,
    });
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full md:max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header with Gradient Background */}
            <div className="bg-gradient1 px-6 py-4 relative flex-shrink-0 rounded-t-lg">
              <h3 className="text-xl font-semibold text-white">
                Subscribe & Save
              </h3>
              {/* Close Button */}
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form Content - Scrollable */}
            <div className="px-6 py-3 overflow-y-auto flex-1">
              <div className="">
                {/* Frequency Selection */}
                <div className="mb-6">
                     <h2 className="text-primary-text text-lg font-semibold pb-3">{product?.product_name || product?.name}</h2>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Delivery Frequency
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {frequencyOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          frequency === option.value
                            ? "border-primary-100 bg-red-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center flex-1">
                          <input
                            type="radio"
                            name="frequency"
                            value={option.value}
                            checked={frequency === option.value}
                            onChange={(e) => setFrequency(e.target.value)}
                            className="w-4 h-4 text-primary-100 border-gray-300"
                          />
                          <span
                            className={`ml-3 text-sm font-medium ${
                              frequency === option.value
                                ? "text-primary-100"
                                : "text-gray-700"
                            }`}
                          >
                            {option.label}
                          </span>
                        </div>
                        {frequency === option.value && (
                          <svg
                            className="w-5 h-5 text-primary-100 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Custom Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Notes
                  </label>
                  <textarea
                    value={customNotes}
                    onChange={(e) => setCustomNotes(e.target.value)}
                    rows="4"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent resize-none"
                    placeholder="Add any special instructions..."
                  />
                </div>

                {/* Product Info */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <div className="space-y-2">
                    {/* Total Price */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Total Price:
                      </span>
                      <span className="text-xl font-bold text-primary-100">
                        ${totalPrice}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button - Fixed at bottom */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-center flex-shrink-0">
              <button
                onClick={handleConfirm}
                className="w-4/6 px-4 py-3 text-lg letter-spacing-1 bg-gradient2 text-white rounded-full font-semibold"
              >
                Subscribe
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default DeliveryPartnerSubscriptionModal;
